/**
 * PaywallModal — Phase 2 / W3.T3 (Weekly Cap + Paywall UI).
 *
 * The Pro-pitch modal. Mounted from three entry points (CONTEXT §entry-points):
 *   1. CapBlockerModal → "Upgrade to Pro" CTA
 *   2. ThemeSelect → tap on a locked theme card
 *   3. AccountMenu → "Upgrade to Pro ✨" dropdown item
 *
 * Phase 2 ships the UX only — `onUpgrade` is a stub that the parent fires
 * as a "Coming soon" toast. Phase 3 wires Stripe Checkout to the same prop.
 *
 * Design rules:
 *   - Always-visible close X top-right (PAY-06). NO timer-gating, no
 *     scroll-hide, no dark patterns.
 *   - Monthly/Yearly is a plan-period TOGGLE (pill control), NOT a "skip
 *     trial" toggle (Pitfall #8 — App Store rejection guard). Trial copy
 *     lives in body, CTA reads "Start free trial".
 *   - Default plan = "yearly" (higher-LTV default; locked decision per
 *     CONTEXT §decisions).
 *   - When plan==="yearly", derived label "€29/yr = €2.42/month" renders
 *     (PAY-07). Monthly view hides that label.
 *   - Backdrop click + ESC + close button all call onClose.
 *   - Inline styles using candy_heart vars (matches SignUpPrompt shell).
 *
 * Props:
 *   - open (bool)    : controls visibility (returns null when false)
 *   - onClose (fn)   : close handler (backdrop, ESC, ✕)
 *   - onUpgrade (fn) : primary CTA — Phase 2 fires toast, Phase 3 fires
 *                      Stripe Checkout
 *   - trigger (str)  : "cap-hit" | "locked-theme" | "account-menu" |
 *                      "unknown" — telemetry only, not used in render
 */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getBilling } from "../services/billing";
import logger from "../services/logger";

// Le pagine legali stanno sul sito: dall'app si aprono nel browser.
const SITO = (process.env.REACT_APP_PUBLIC_URL || "https://puzzly.giadalabs.com").replace(/\/$/, "");
export const ATTESA_OFFERTE_MS = 8000;

export default function PaywallModal({
  open,
  onClose,
  onUpgrade,
  onRestore = null,
  trigger = "unknown",
  errore = "",
}) {
  const { t } = useTranslation();
  const [plan, setPlan] = useState("yearly");
  // I prodotti arrivano dal servizio billing (gli store, via RevenueCat).
  // Lista vuota = qui non si compra ancora: niente prezzi, niente pulsante. È così che il paywall si adegua da solo quando l'acquisto
  // in-app si accende, senza che nessuno torni a toccare questo file.
  const [prodotti, setProdotti] = useState(null);
  // Esito di «Ripristina acquisti» (#38): idle | busy | ok | none | error.
  const [ripristino, setRipristino] = useState("idle");

  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setRipristino("idle");
    const billing = getBilling();
    // Se le offerte non arrivano il paywall non deve restare vuoto e muto: dopo
    // ATTESA_OFFERTE_MS lo dice, e il log riporta dove si è fermato (21/10).
    const attesa = setTimeout(() => {
      logger.error("billing: offerte ancora in attesa", { error: `fase: ${billing.fase || "?"}` });
      if (vivo) setProdotti((p) => (p === null ? [] : p));
    }, ATTESA_OFFERTE_MS);
    billing
      .getProducts()
      .then((lista) => {
        clearTimeout(attesa);
        if (vivo) setProdotti(lista);
      })
      .catch((err) => {
        clearTimeout(attesa);
        // Senza questo log il paywall restava senza pulsanti e senza un perché (21/10).
        logger.error("billing: offerte non caricate", { error: err && err.message, code: err && err.code });
        if (vivo) setProdotti([]);
      });
    return () => {
      vivo = false;
    };
  }, [open]);

  // ESC dismisses (PAY-06 ambient — close paths must be many). Effect always
  // runs (rules-of-hooks); inner guard skips when closed.
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [open, onClose]);

  if (!open) return null;
  const prodotto = (prodotti || []).find((x) => x.plan === plan) || null;
  const siCompraQui = prodotti !== null && prodotti.length > 0;

  // ── Inline styles (candy_heart, no-line rule) ──
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    zIndex: 330,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  };
  const cardStyle = {
    background: "var(--color-surface)",
    borderRadius: "2rem",
    padding: 32,
    maxWidth: 480,
    width: "100%",
    maxHeight: "92vh",
    overflowY: "auto",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    position: "relative",
  };
  const closeBtnStyle = {
    position: "absolute",
    top: 16,
    right: 16,
    background: "var(--color-bgAlt)",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "var(--color-textMuted)",
    fontSize: 16,
    fontWeight: 700,
    zIndex: 1,
  };
  const titleStyle = {
    fontFamily: "var(--font-display)",
    fontSize: 26,
    fontWeight: 800,
    color: "var(--color-text)",
    margin: "0 0 8px",
    textAlign: "center",
  };
  const subtitleStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 14,
    color: "var(--color-textMuted)",
    textAlign: "center",
    margin: "0 0 20px",
  };
  const pillContainerStyle = {
    display: "flex",
    gap: 6,
    padding: 4,
    background: "var(--color-bgAlt)",
    borderRadius: 14,
    margin: "0 auto 20px",
    maxWidth: 280,
  };
  const pillBase = {
    flex: 1,
    padding: "10px 16px",
    border: "none",
    borderRadius: 10,
    fontFamily: "var(--font-body)",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.18s ease",
  };
  const activePill = {
    ...pillBase,
    background:
      "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
    color: "var(--color-onPrimary)",
    boxShadow: "0 4px 12px var(--color-glow)",
  };
  const inactivePill = {
    ...pillBase,
    background: "transparent",
    color: "var(--color-textMuted)",
  };
  const priceRowStyle = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: 6,
    margin: "0 0 4px",
  };
  const bigPriceStyle = {
    fontFamily: "var(--font-display)",
    fontSize: 44,
    fontWeight: 800,
    color: "var(--color-primary)",
    lineHeight: 1,
  };
  const perStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 16,
    color: "var(--color-textMuted)",
    fontWeight: 600,
  };
  const equivStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 13,
    color: "var(--color-textMuted)",
    textAlign: "center",
    margin: "0 0 18px",
    fontWeight: 500,
  };
  const benefitsStyle = {
    listStyle: "none",
    padding: 0,
    margin: "0 0 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  };
  const benefitItemStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 14,
    color: "var(--color-text)",
    lineHeight: 1.4,
  };
  const trialStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 13,
    color: "var(--color-textMuted)",
    textAlign: "center",
    margin: "0 0 6px",
    lineHeight: 1.5,
  };
  const renewalStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 12,
    // textMuted, not textDim: small print must stay ≥ 4.5:1 on every theme's
    // surface (Christmas textDim on white is 2.96:1).
    color: "var(--color-textMuted)",
    textAlign: "center",
    margin: "0 0 18px",
    lineHeight: 1.5,
  };
  const ctaPrimaryStyle = {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: 16,
    fontFamily: "var(--font-body)",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    background:
      "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
    color: "var(--color-onPrimary)",
    boxShadow: "0 8px 24px var(--color-glow)",
  };
  const linkStyle = {
    display: "block",
    margin: "12px auto 0",
    background: "none",
    border: "none",
    color: "var(--color-textMuted)",
    fontFamily: "var(--font-body)",
    fontSize: 14,
    textDecoration: "underline",
    cursor: "pointer",
  };
  const legaleStyle = { color: "var(--color-textMuted)" };

  return (
    <div
      onClick={onClose}
      style={overlayStyle}
      data-trigger={trigger}
      data-testid="paywall-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="paywall-title"
      >
        <button
          type="button"
          onClick={onClose}
          style={closeBtnStyle}
          aria-label={t("ui.paywall.closeAria")}
        >
          ✕
        </button>

        <h2 id="paywall-title" style={titleStyle}>
          {t("ui.paywall.title")}
        </h2>
        <p style={subtitleStyle}>
          {t("ui.paywall.subtitle")}
        </p>

        {/* Plan-period toggle — NOT a trial toggle (Pitfall #8). Si sceglie il
            periodo solo dove si può comprare: altrove sarebbe una scelta finta. */}
        {siCompraQui && (
        <div
          style={pillContainerStyle}
          role="tablist"
          aria-label={t("ui.paywall.planPeriodAria")}
        >
          <button
            type="button"
            role="tab"
            aria-selected={plan === "monthly"}
            onClick={() => setPlan("monthly")}
            style={plan === "monthly" ? activePill : inactivePill}
          >
            {t("ui.paywall.monthly")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={plan === "yearly"}
            onClick={() => setPlan("yearly")}
            style={plan === "yearly" ? activePill : inactivePill}
          >
            {t("ui.paywall.yearly")}
          </button>
        </div>
        )}

        {prodotto && (
        <div style={priceRowStyle}>
          <span style={bigPriceStyle}>{prodotto.price}</span>
          <span style={perStyle}>/{t(`ui.plans.${plan}.per`)}</span>
        </div>
        )}
        {prodotto && prodotto.hasMonthlyEquiv && (
          <p style={equivStyle}>
            {t(`ui.plans.${plan}.monthlyEquiv`, { price: prodotto.price, perMonth: prodotto.perMonth })}
          </p>
        )}

        <ul style={benefitsStyle}>
          <li style={benefitItemStyle}>{t("ui.paywall.benefitThemes")}</li>
          <li style={benefitItemStyle}>{t("ui.paywall.benefitSurprises")}</li>
          <li style={{ ...benefitItemStyle, fontWeight: 700, color: "var(--color-primary)" }}>
            {t("ui.paywall.benefitSlots")}
          </li>
          <li style={benefitItemStyle}>{t("ui.paywall.benefitEarlyAccess")}</li>
          <li style={benefitItemStyle}>{t("ui.paywall.benefitSupport")}</li>
        </ul>

        {/* Prova e prezzi vengono dal prodotto dello store: una prova che lo
            store non dà, o un prezzo diverso da quello pagato, sono un rifiuto. */}
        {prodotto && (
          <p style={trialStyle} data-testid="paywall-terms-line">
            {prodotto.trial
              ? t(`ui.plans.${plan}.trial`, {
                  price: prodotto.price,
                  perMonth: prodotto.perMonth,
                  period: t(`ui.paywall.trialPeriod.${prodotto.trial.unit}`, { count: prodotto.trial.count }),
                })
              : t(`ui.plans.${plan}.noTrial`, { price: prodotto.price, perMonth: prodotto.perMonth })}
          </p>
        )}
        {siCompraQui && (
        <p style={renewalStyle}>
          {t("ui.paywall.autoRenew", {
            renewalPeriod: t(`ui.plans.${plan}.renewalPeriod`),
          })}
        </p>
        )}

        {errore && (
          <p role="alert" data-testid="paywall-error" style={{ ...renewalStyle, color: "var(--color-wrong)" }}>
            {t("ui.paywall.purchaseFailed")} {errore}
          </p>
        )}
        {prodotti !== null && !siCompraQui ? (
          <p style={renewalStyle} data-testid="paywall-native-note">
            {t("ui.paywall.proOnAccount")}
          </p>
        ) : siCompraQui ? (
        <button
          type="button"
          onClick={() => onUpgrade && onUpgrade(plan)}
          style={ctaPrimaryStyle}
          data-testid="paywall-start-trial"
        >
          {t(prodotto && prodotto.trial ? "ui.paywall.startTrial" : "ui.paywall.subscribe")}
        </button>
        ) : null}

        {/* Apple (3.1.1, 3.1.2): ripristino degli acquisti, e termini e privacy
            raggiungibili da dove ci si abbona. */}
        {onRestore && prodotti !== null && (
          <button
            type="button"
            onClick={async () => {
              setRipristino("busy");
              try {
                const esito = await onRestore();
                setRipristino(esito === "ok" ? "ok" : esito === "none" ? "none" : "idle");
              } catch (_) {
                setRipristino("error");
              }
            }}
            disabled={ripristino === "busy"}
            style={linkStyle}
            data-testid="paywall-restore"
          >
            {t(ripristino === "busy" ? "ui.paywall.restoring" : "ui.paywall.restore")}
          </button>
        )}
        {["ok", "none", "error"].includes(ripristino) && (
          <p style={renewalStyle} role="status" data-testid="paywall-restore-result">
            {t(`ui.paywall.restore_${ripristino}`)}
          </p>
        )}
        <p style={{ ...renewalStyle, marginTop: 4 }}>
          <a href={`${SITO}/terms`} target="_blank" rel="noopener noreferrer" style={legaleStyle}>{t("ui.paywall.terms")}</a>
          {" · "}
          <a href={`${SITO}/privacy`} target="_blank" rel="noopener noreferrer" style={legaleStyle}>{t("ui.paywall.privacy")}</a>
        </p>
      </div>
    </div>
  );
}
